import React, { useState, useRef } from 'react';
import { collection, Timestamp, query, where, getDocs, writeBatch, doc } from 'firebase/firestore';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Link } from 'react-router-dom';
import { db } from '../firebase';

const AnchorSVG = ({ className = '' }: { className?: string }) => (
  <svg className={className} viewBox="0 0 40 52" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="20" cy="7" r="5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
    <circle cx="20" cy="7" r="2" fill="currentColor"/>
    <line x1="4" y1="14" x2="36" y2="14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="20" y1="12" x2="20" y2="40" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M20 40 C13 38 5 33 5 26" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
    <path d="M20 40 C27 38 35 33 35 26" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
    <path d="M5 26 C5 46 35 46 35 26" stroke="currentColor" strokeWidth="1.5" fill="none"/>
  </svg>
);

interface GuestData {
  id: string;
  name: string;
  nameLowercase: string;
  familyId: string;
  familyName: string;
}

interface FamilyMemberRSVP {
  guestId: string;
  guestName: string;
  attending: 'yes' | 'no' | 'pending';
  attendingAfterparty: 'yes' | 'no' | 'pending';
  email: string;
  phone: string;
  dietaryRestrictions: string;
  danceSong: string;
  message: string;
}

const RSVP: React.FC = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [invitationFound, setInvitationFound] = useState(false);
  const [searchName, setSearchName] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<GuestData[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [isFuzzyMatch, setIsFuzzyMatch] = useState(false);
  const [guestData, setGuestData] = useState<GuestData | null>(null);
  const [invitationError, setInvitationError] = useState('');

  const [familyRSVPs, setFamilyRSVPs] = useState<FamilyMemberRSVP[]>([]);
  const originalRSVPs = useRef<FamilyMemberRSVP[]>([]);
  const touchedGuestIds = useRef<Set<string>>(new Set());

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Simple similarity score based on common words and character overlap
  const calculateSimilarity = (searchTerm: string, guestName: string): number => {
    const searchLower = searchTerm.toLowerCase().trim();
    const nameLower = guestName.toLowerCase().trim();

    // Exact match gets highest score
    if (nameLower === searchLower) return 100;

    // Contains search term gets high score
    if (nameLower.includes(searchLower)) return 90;

    // Split into words for fuzzy matching
    const searchWords = searchLower.split(/\s+/);
    const nameWords = nameLower.split(/\s+/);

    let score = 0;

    // Check if any search words match any name words (partial match)
    for (const searchWord of searchWords) {
      for (const nameWord of nameWords) {
        if (nameWord.includes(searchWord) || searchWord.includes(nameWord)) {
          score += 50;
        } else {
          // Calculate character overlap (Levenshtein-like simple approach)
          let matches = 0;
          const minLength = Math.min(searchWord.length, nameWord.length);
          for (let i = 0; i < minLength; i++) {
            if (searchWord[i] === nameWord[i]) matches++;
          }
          const overlapRatio = matches / Math.max(searchWord.length, nameWord.length);
          if (overlapRatio > 0.5) score += 30 * overlapRatio;
        }
      }
    }

    return score;
  };

  const handleInvitationSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setSearching(true);
    setInvitationError('');
    setShowResults(false);

    try {
      // Fetch ALL guests and filter on client side for partial matching
      const querySnapshot = await getDocs(collection(db, 'guestList'));
      const allGuests: GuestData[] = [];
      querySnapshot.forEach((doc) => {
        allGuests.push({ id: doc.id, ...doc.data() } as GuestData);
      });

      // Calculate similarity scores for all guests
      const guestsWithScores = allGuests.map(guest => ({
        guest,
        score: calculateSimilarity(searchName, guest.name)
      }));

      // Filter for good matches (score > 0)
      const goodMatches = guestsWithScores
        .filter(item => item.score > 0)
        .sort((a, b) => b.score - a.score);

      // Exact or strong matches (score >= 50)
      const strongMatches = goodMatches.filter(item => item.score >= 50);

      // Fuzzy matches for "Did you mean?" (score 20-49)
      const fuzzyMatches = goodMatches.filter(item => item.score >= 20 && item.score < 50);

      if (strongMatches.length === 0) {
        if (fuzzyMatches.length > 0) {
          // Show "Did you mean?" suggestions
          setSearchResults(fuzzyMatches.slice(0, 5).map(item => item.guest));
          setShowResults(true);
          setIsFuzzyMatch(true);
        } else {
          setInvitationError('Please reach out to (517) 668-5268 if you are unable to find your invitation.');
          setSearchResults([]);
          setIsFuzzyMatch(false);
        }
      } else {
        // Show strong match results for user to choose
        setSearchResults(strongMatches.map(item => item.guest));
        setShowResults(true);
        setIsFuzzyMatch(false);
      }
    } catch (err) {
      console.error('Error searching for invitation:', err);
      setInvitationError('An error occurred while searching. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  const selectGuest = async (guest: GuestData) => {
    setGuestData(guest);

    let members: GuestData[];
    if (!guest.familyId) {
      // Individual guest — no family query needed
      members = [guest];
    } else {
      // Fetch all family members
      const familyQuery = query(
        collection(db, 'guestList'),
        where('familyId', '==', guest.familyId)
      );
      const familySnapshot = await getDocs(familyQuery);
      members = [];
      familySnapshot.forEach((doc) => {
        members.push({ id: doc.id, ...doc.data() } as GuestData);
      });
      members.sort((a, b) => a.name.localeCompare(b.name));
    }

    // Check for existing RSVPs for this family
    const memberIds = members.map(m => m.id);
    const existingRSVPsQuery = query(
      collection(db, 'rsvps'),
      where('guestId', 'in', memberIds.slice(0, 10)) // Firestore 'in' limit is 10
    );
    const existingRSVPsSnapshot = await getDocs(existingRSVPsQuery);
    const existingRSVPsMap: { [key: string]: any } = {};
    existingRSVPsSnapshot.forEach((doc) => {
      const data = doc.data();
      existingRSVPsMap[data.guestId] = data;
    });

    // If we have more than 10 members, fetch remaining RSVPs
    if (memberIds.length > 10) {
      for (let i = 10; i < memberIds.length; i += 10) {
        const batch = memberIds.slice(i, i + 10);
        const batchQuery = query(collection(db, 'rsvps'), where('guestId', 'in', batch));
        const batchSnapshot = await getDocs(batchQuery);
        batchSnapshot.forEach((doc) => {
          const data = doc.data();
          existingRSVPsMap[data.guestId] = data;
        });
      }
    }

    // Initialize RSVP state for each family member, using existing data if available
    const initialRSVPs: FamilyMemberRSVP[] = members.map(member => {
      const existing = existingRSVPsMap[member.id];
      if (existing) {
        // Pre-fill with existing RSVP data
        return {
          guestId: member.id,
          guestName: member.name,
          attending: existing.attending || 'pending',
          attendingAfterparty: existing.attendingAfterparty || 'pending',
          email: existing.email || '',
          phone: existing.phone || '',
          dietaryRestrictions: existing.dietaryRestrictions || '',
          danceSong: existing.danceSong || '',
          message: existing.message || ''
        };
      }
      return {
        guestId: member.id,
        guestName: member.name,
        attending: 'pending',
        attendingAfterparty: 'pending',
        email: '',
        phone: '',
        dietaryRestrictions: '',
        danceSong: '',
        message: ''
      };
    });
    originalRSVPs.current = initialRSVPs.map(r => ({ ...r }));
    touchedGuestIds.current = new Set();
    setFamilyRSVPs(initialRSVPs);
    setShowResults(false);
    setInvitationFound(true);
  };

  const updateFamilyMemberRSVP = (guestId: string, field: string, value: string) => {
    touchedGuestIds.current.add(guestId);
    setFamilyRSVPs(prev => prev.map(rsvp =>
      rsvp.guestId === guestId ? { ...rsvp, [field]: value } : rsvp
    ));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    // Only submit entries the user explicitly interacted with
    const changedRSVPs = familyRSVPs.filter(rsvp =>
      touchedGuestIds.current.has(rsvp.guestId)
    );

    if (changedRSVPs.length === 0) {
      setError('No changes detected. Please update your information before submitting.');
      setSubmitting(false);
      return;
    }

    // Validate only the changed entries
    const attendingWithoutEmail = changedRSVPs.filter(
      rsvp => rsvp.attending === 'yes' && !rsvp.email.trim()
    );
    if (attendingWithoutEmail.length > 0) {
      setError(`Please enter an email address for all attending guests. Missing for: ${attendingWithoutEmail.map(r => r.guestName).join(', ')}`);
      setSubmitting(false);
      return;
    }

    const attendingWithoutDietary = changedRSVPs.filter(
      rsvp => rsvp.attending === 'yes' && !rsvp.dietaryRestrictions.trim()
    );
    if (attendingWithoutDietary.length > 0) {
      setError(`Please enter dietary restrictions for all attending guests (or enter "None"). Missing for: ${attendingWithoutDietary.map(r => r.guestName).join(', ')}`);
      setSubmitting(false);
      return;
    }

    // Execute reCAPTCHA v3
    if (!executeRecaptcha) {
      setError('reCAPTCHA not loaded. Please refresh the page and try again.');
      setSubmitting(false);
      return;
    }

    try {
      const recaptchaToken = await executeRecaptcha('rsvp_submit');

      // Note: The token should ideally be verified on the backend
      // For now, we're just generating it for spam protection
      console.log('reCAPTCHA token generated:', recaptchaToken);
      // Check for existing RSVPs to update instead of duplicate
      const memberIds = changedRSVPs.map(r => r.guestId);
      const existingRSVPsQuery = query(
        collection(db, 'rsvps'),
        where('guestId', 'in', memberIds.slice(0, 10))
      );
      const existingRSVPsSnapshot = await getDocs(existingRSVPsQuery);
      const existingRSVPsMap: { [key: string]: string } = {};
      existingRSVPsSnapshot.forEach((doc) => {
        const data = doc.data();
        existingRSVPsMap[data.guestId] = doc.id; // Store document ID
      });

      // Handle remaining members if more than 10
      if (memberIds.length > 10) {
        for (let i = 10; i < memberIds.length; i += 10) {
          const batch = memberIds.slice(i, i + 10);
          const batchQuery = query(collection(db, 'rsvps'), where('guestId', 'in', batch));
          const batchSnapshot = await getDocs(batchQuery);
          batchSnapshot.forEach((doc) => {
            const data = doc.data();
            existingRSVPsMap[data.guestId] = doc.id;
          });
        }
      }

      const batch = writeBatch(db);

      // Create or update only the entries that actually changed
      changedRSVPs.forEach(rsvp => {
        const rsvpData = {
          guestId: rsvp.guestId,
          guestName: rsvp.guestName,
          familyId: guestData?.familyId,
          email: rsvp.email,
          phone: rsvp.phone,
          attending: rsvp.attending,
          attendingAfterparty: rsvp.attendingAfterparty,
          dietaryRestrictions: rsvp.dietaryRestrictions,
          danceSong: rsvp.danceSong,
          message: rsvp.message,
          submittedAt: Timestamp.now()
        };

        if (existingRSVPsMap[rsvp.guestId]) {
          // Update existing RSVP
          const rsvpRef = doc(db, 'rsvps', existingRSVPsMap[rsvp.guestId]);
          batch.set(rsvpRef, rsvpData, { merge: true });
        } else {
          // Create new RSVP
          const rsvpRef = doc(collection(db, 'rsvps'));
          batch.set(rsvpRef, rsvpData);
        }
      });

      await batch.commit();
      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting RSVP:', err);
      setError('There was an error submitting your RSVP. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main>
      {/* PAGE HEADER */}
      <div className="page-header">
        <AnchorSVG className="page-header__anchor" />
        <p className="page-header__eyebrow">Join the Celebration</p>
        <h1 className="page-header__title">RSVP</h1>
        <div className="page-header__rule page-rule">
          <div className="rule-line rule-line--short"></div>
          <div className="rule-diamond"></div>
          <div className="rule-line rule-line--short"></div>
        </div>
      </div>

      {/* RSVP CONTENT */}
      <section className="rsvp-section">
        <div className="rsvp-container">
          {!invitationFound ? (
            <div className="rsvp-search-card">
              <p className="rsvp-search-card__intro">
                Please enter your name to find your invitation
              </p>
              <form onSubmit={handleInvitationSearch} className="rsvp-search-form">
                <div className="form-group">
                  <label htmlFor="searchName">Full Name *</label>
                  <input
                    type="text"
                    id="searchName"
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                    required
                    placeholder="Enter your name (or part of it)"
                  />
                </div>
                {invitationError && (
                  <div className="error-message">
                    {invitationError}
                  </div>
                )}
                <button
                  type="submit"
                  className="cta-button"
                  disabled={searching}
                >
                  {searching ? 'Searching...' : 'Find My Invitation'}
                </button>
              </form>

              {showResults && searchResults.length > 0 && (
                <div className="rsvp-results">
                  {isFuzzyMatch ? (
                    <>
                      <h3 className="rsvp-results__heading">
                        No exact matches found
                      </h3>
                      <p className="rsvp-results__subtext">
                        Did you mean one of these?
                      </p>
                    </>
                  ) : (
                    <>
                      <h3 className="rsvp-results__heading">
                        Match Found!
                      </h3>
                      <p className="rsvp-results__subtext">
                        Select your name to continue:
                      </p>
                    </>
                  )}
                  <div className="rsvp-results__list">
                    {searchResults.map((guest) => (
                      <button
                        key={guest.id}
                        onClick={() => selectGuest(guest)}
                        className="rsvp-results__item"
                      >
                        <div className="rsvp-results__item-name">{guest.name}</div>
                        <div className="rsvp-results__item-family">
                          {guest.familyName}
                        </div>
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => {
                      setShowResults(false);
                      setSearchResults([]);
                      setSearchName('');
                      setIsFuzzyMatch(false);
                    }}
                    className="rsvp-results__back-btn"
                  >
                    Search Again
                  </button>
                </div>
              )}
            </div>
          ) : submitted ? (
            <div className="rsvp-success-card">
              <h2 className="rsvp-success-card__heading">Thank You!</h2>
              <p className="rsvp-success-card__message">Your RSVP has been submitted successfully. We can't wait to celebrate with you!</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="rsvp-form">
              <div className="rsvp-form__header">
                <h2 className="rsvp-form__title">
                  {guestData?.familyName || 'Your Family'}
                </h2>
                <button
                  type="button"
                  onClick={() => {
                    setInvitationFound(false);
                    setGuestData(null);
                    setFamilyRSVPs([]);
                    setSearchName('');
                    setSearchResults([]);
                    setShowResults(false);
                    setError('');
                  }}
                  className="rsvp-form__back-btn"
                >
                  ← Back to Search
                </button>
              </div>
              <p className="rsvp-form__intro">
                Please RSVP for yourself and any family members or plus-ones. Only RSVP for others if you're certain of their response. They can always RSVP themselves!
              </p>

              {familyRSVPs.map((rsvp, index) => (
                <div key={rsvp.guestId} className="rsvp-member-card">
                  <h3 className="rsvp-member-card__name">{rsvp.guestName}</h3>

                  <div className="form-group">
                    <label htmlFor={`attending-${index}`}>Will you be attending? *</label>
                    <select
                      id={`attending-${index}`}
                      value={rsvp.attending}
                      onChange={(e) => updateFamilyMemberRSVP(rsvp.guestId, 'attending', e.target.value)}
                      required
                    >
                      <option value="pending">I don't know yet</option>
                      <option value="yes">Yes, I'll be there!</option>
                      <option value="no">Sorry, I can't make it</option>
                    </select>
                  </div>

                  {rsvp.attending === 'yes' && (
                    <>
                      <div className="form-group">
                        <label htmlFor={`email-${index}`}>Email Address *</label>
                        <input
                          type="email"
                          id={`email-${index}`}
                          value={rsvp.email}
                          onChange={(e) => updateFamilyMemberRSVP(rsvp.guestId, 'email', e.target.value)}
                          required
                          placeholder="your.email@example.com"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor={`phone-${index}`}>Phone Number</label>
                        <input
                          type="tel"
                          id={`phone-${index}`}
                          value={rsvp.phone}
                          onChange={(e) => updateFamilyMemberRSVP(rsvp.guestId, 'phone', e.target.value)}
                          placeholder="(555) 555-5555"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor={`dietary-${index}`}>
                          Dietary Restrictions or Allergies *
                          <span className="label-hint">
                            (Enter "None" if no restrictions)
                          </span>
                        </label>
                        <textarea
                          id={`dietary-${index}`}
                          value={rsvp.dietaryRestrictions}
                          onChange={(e) => updateFamilyMemberRSVP(rsvp.guestId, 'dietaryRestrictions', e.target.value)}
                          rows={2}
                          placeholder="Please let us know of any dietary restrictions... or enter 'None'"
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor={`afterparty-${index}`}>
                          Will you be attending the afterparty?
                          <span className="label-hint">
                            More information can be found under <Link to="/qa#afterparty" style={{color: 'var(--gold)', textDecoration: 'underline'}}>Q &amp; A</Link>
                          </span>
                        </label>
                        <select
                          id={`afterparty-${index}`}
                          value={rsvp.attendingAfterparty}
                          onChange={(e) => updateFamilyMemberRSVP(rsvp.guestId, 'attendingAfterparty', e.target.value)}
                        >
                          <option value="pending">Select an option</option>
                          <option value="yes">Yes, I'll be there!</option>
                          <option value="no">No, I won't be attending</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label htmlFor={`dance-${index}`}>
                          What song would get you on the dance floor?
                          <span className="label-hint">
                            (Optional)
                          </span>
                        </label>
                        <input
                          type="text"
                          id={`dance-${index}`}
                          value={rsvp.danceSong}
                          onChange={(e) => updateFamilyMemberRSVP(rsvp.guestId, 'danceSong', e.target.value)}
                          placeholder="Song title and artist (optional)"
                        />
                      </div>
                    </>
                  )}

                  {(rsvp.attending === 'yes' || rsvp.attending === 'no') && (
                    <div className="form-group">
                      <label htmlFor={`message-${index}`}>
                        Message to the Couple
                        <span className="label-hint">
                          (Optional)
                        </span>
                      </label>
                      <textarea
                        id={`message-${index}`}
                        value={rsvp.message}
                        onChange={(e) => updateFamilyMemberRSVP(rsvp.guestId, 'message', e.target.value)}
                        rows={3}
                        placeholder="Share your excitement or leave a message..."
                      />
                    </div>
                  )}
                </div>
              ))}

              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              <p className="rsvp-form__notice">
                If you were expecting to bring a plus-one or an additional family member and you don't see their invitation please reach out to (517) 668-5268 after July 1st and we'll see if we can accommodate!
              </p>

              <button
                type="submit"
                className="cta-button"
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Submit RSVP'}
              </button>
            </form>
          )}
        </div>
      </section>
    </main>
  );
};

export default RSVP;
